import { later } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { APIData, Service } from 'webapp-types';
import { getTs } from 'webapp/utils/common';

// Integrations selection popup (Experiment 420231) - used only A variation
// https://app.asana.com/0/527984279702626/1203943931826006/f

const TS_RELEASE_INTEGRATION_SELECTION_POPUP = '2023-08-10 13:00:00';

export default class FlowIntegrationsSelectionPopup extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked wasPopupShown = false; // used to prevent showing popup in same "session"
	isEligibleToCheckToOpenPopup = true; // prevent check when coming from "+ New connection" button, another check is also in routes/datamanager/available.ts:35

	private openAccountIntegrationsSelectionPopup(): void {
		this.accountService.openAccountIntegrationsSelectionPopup({
			origin: 'datamanager',
		});
	}

	checkToOpenPopup(): void {
		if (!this.isEligibleToCheckToOpenPopup) {
			this.isEligibleToCheckToOpenPopup = true;
			return;
		}

		if (this.wasPopupShown) {
			return;
		}

		if (this.currentUserService.hasDataboxEmail) {
			return;
		}

		if (
			this.accountService.whiteLabelingEnabled ||
			this.accountService.isClient ||
			this.currentUserService.isViewer
		) {
			return;
		}

		if (this.popupService.anyPopupsOpen) {
			return;
		}

		// Check if existing users has already at least 1 connected (and additional default Token)
		const created = this.currentUserService.model?.created;
		if (
			this.mmSourcesService.savedRecords.length < 2 &&
			created &&
			created < getTs(TS_RELEASE_INTEGRATION_SELECTION_POPUP)
		) {
			return;
		}

		this.requestService
			.api<APIData.Account.Check.CompanySourcesSelection>(
				'GET',
				'/account/check/company-sources-selection',
			)
			.then((response) => {
				const isEligibleToShowPopup = response.isEligible;

				if (isEligibleToShowPopup) {
					this.openAccountIntegrationsSelectionPopup();
				}
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	checkToOpenPopupWithDelay(delay = 1000) {
		later(() => {
			this.checkToOpenPopup();
		}, delay);
	}
}

declare module '@ember/service' {
	interface Registry {
		'flow/integrations-selection-popup': FlowIntegrationsSelectionPopup;
	}
}
